import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { blogPosts } from '../../../mocks/blogPosts';

interface BlogGridProps {
  searchQuery: string;
}

const categories = [
  'All',
  'Social Media',
  'Content Strategy',
  'SEO',
  'Paid Ads',
  'Analytics',
  'Branding',
];

export default function BlogGrid({ searchQuery }: BlogGridProps) {
  const [activeCategory, setActiveCategory] = useState('All');
  const { ref, isVisible } = useScrollAnimation();

  const today = new Date();
  today.setHours(23, 59, 59, 999);

  // Sort posts by date (newest first) and exclude featured post and future posts
  const displayPosts = blogPosts
    .filter(post => post.id !== 7)
    .filter(post => new Date(post.date) <= today)
    .sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB.getTime() - dateA.getTime();
    });

  const filteredPosts = displayPosts.filter((post) => {
    const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
    if (!searchQuery) return matchesCategory;
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      post.title.toLowerCase().includes(q) ||
      post.excerpt.toLowerCase().includes(q) ||
      post.category.toLowerCase().includes(q) ||
      post.author.toLowerCase().includes(q);
    return matchesCategory && matchesSearch;
  });

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Social Media': 'bg-pink-100 text-pink-700',
      'Content Strategy': 'bg-emerald-100 text-emerald-700',
      'SEO': 'bg-sky-100 text-sky-700',
      'Paid Ads': 'bg-amber-100 text-amber-700',
      'Analytics': 'bg-violet-100 text-violet-700',
      'Branding': 'bg-rose-100 text-rose-700',
    };
    return colors[category] || 'bg-slate-100 text-slate-700';
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div
          ref={ref}
          className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-12">
            <h2 className="font-['Montserrat'] text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              {searchQuery ? `Results for "${searchQuery}"` : 'Latest Articles'}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {searchQuery
                ? `${filteredPosts.length} article${filteredPosts.length !== 1 ? 's' : ''} found matching your search.`
                : 'Explore our collection of marketing insights, tips, and strategies to grow your brand.'}
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === category
                    ? 'bg-[#00989e] text-white shadow-lg shadow-[#00989e]/30'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {filteredPosts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className={`group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100 cursor-pointer ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-52 object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getCategoryColor(post.category)}`}>
                        {post.category}
                      </span>
                      <span className="text-slate-400 text-xs">{post.readTime} read</span>
                    </div>

                    <h3 className="font-['Montserrat'] text-lg font-bold text-slate-800 mb-3 line-clamp-2 group-hover:text-[#00989e] transition-colors">
                      {post.title}
                    </h3>

                    <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                      {post.excerpt}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <div className="flex items-center gap-3">
                        <img
                          src={post.authorImage}
                          alt={post.author}
                          className="w-8 h-8 rounded-full object-cover object-top"
                        />
                        <span className="text-sm text-slate-600">{post.author}</span>
                      </div>
                      <span className="text-xs text-slate-400">{post.date}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-slate-100 rounded-full">
                <i className="ri-search-line text-2xl text-slate-400"></i>
              </div>
              <h3 className="font-['Montserrat'] text-xl font-bold text-slate-700 mb-2">
                No articles found
              </h3>
              <p className="text-slate-500 max-w-md mx-auto">
                We couldn&apos;t find any articles matching &ldquo;{searchQuery}&rdquo;{activeCategory !== 'All' ? ` in ${activeCategory}` : ''}. Try a different keyword or category.
              </p>
            </div>
          )}

          {filteredPosts.length > 0 && (
            <div className="text-center mt-12">
              <button className="inline-flex items-center gap-2 px-8 py-3 border-2 border-[#00989e] text-[#00989e] font-semibold rounded-full hover:bg-[#00989e] hover:text-white transition-all cursor-pointer whitespace-nowrap">
                Load More Articles
                <i className="ri-arrow-down-line"></i>
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
